/**
 * Dashboard de inicio de sa.calbucura.cl — Sprint 13 S13-26.
 *
 * Reemplaza la HomePage estatica anterior. Consume GET /dashboard/sa
 * y muestra metricas globales del estado del barrio: casas, personas,
 * usuarios, tenencias, recursos por tipo, cuotas por estado, roles
 * vigentes, configuracion financiera, proyectos.
 *
 * Cada card es clickeable y lleva al mantenedor correspondiente.
 *
 * RBAC ruta: super_admin (sa).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api/client';
import { getDashboardSa, type DashboardSa } from '../lib/api/dashboard';
import { formatCLP } from '../lib/format';
import { PageHeader } from '../components/layout/PageHeader';

interface MetricaCardProps {
  titulo: string;
  valor: number | string;
  subtitulo?: string;
  to?: string;
  acento?: 'default' | 'success' | 'warning' | 'danger';
}

function MetricaCard({
  titulo,
  valor,
  subtitulo,
  to,
  acento = 'default',
}: MetricaCardProps) {
  const borderColor = {
    default: 'border-white/[0.08]',
    success: 'border-state-success/40',
    warning: 'border-state-warning/40',
    danger: 'border-state-danger/40',
  }[acento];
  const valorColor = {
    default: 'text-text-primary',
    success: 'text-state-success',
    warning: 'text-state-warning',
    danger: 'text-state-danger',
  }[acento];

  const body = (
    <div
      className={`bg-bg-card border-[0.5px] ${borderColor} rounded-md p-4 hover:bg-bg-elevated transition-colors h-full`}
    >
      <div className="text-xs uppercase tracking-wide text-text-secondary font-medium">
        {titulo}
      </div>
      <div className={`text-3xl font-medium mt-2 ${valorColor}`}>{valor}</div>
      {subtitulo && (
        <div className="text-xs text-text-muted mt-1">{subtitulo}</div>
      )}
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="block">
        {body}
      </Link>
    );
  }
  return body;
}

interface SeccionProps {
  titulo: string;
  children: React.ReactNode;
}

function Seccion({ titulo, children }: SeccionProps) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm uppercase tracking-wide text-text-secondary font-medium">
        {titulo}
      </h2>
      {children}
    </section>
  );
}

export function Dashboard() {
  const [data, setData] = useState<DashboardSa | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    getDashboardSa()
      .then((d) => {
        if (cancelado) return;
        setData(d);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar dashboard`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  return (
    <div className="px-8 py-6 space-y-8">
      <PageHeader
        eyebrow="Panel Super Admin"
        title="Barrio Calbucura"
        description="Visión global del estado del sistema. Las acciones de mantenimiento están en el menú superior; el día a día se opera desde el portal de la Comisión / Tesorería."
      />

      {loading && <p className="text-sm text-text-muted">Cargando métricas…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          <Seccion titulo="Inventario del barrio">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricaCard
                titulo="Casas"
                valor={data.casas.total}
                subtitulo={`${data.casas.activas} activas`}
                to="/superadmin/lotes"
              />
              <MetricaCard
                titulo="Personas"
                valor={data.personas.total}
                subtitulo={`${data.personas.activas} activas · ${data.personas.con_usuario_operativo} con cuenta`}
                to="/superadmin/personas"
              />
              <MetricaCard
                titulo="Tenencias activas"
                valor={data.tenencias.activas}
                subtitulo={`${data.tenencias.total} en historial`}
                to="/superadmin/tenencias"
              />
              <MetricaCard
                titulo="Usuarios operativos"
                valor={data.usuarios.operativos_activos}
                subtitulo={`+ ${data.usuarios.super_admin} super admin`}
              />
            </div>
          </Seccion>

          <Seccion titulo="Roles vigentes">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricaCard
                titulo="Vecinos"
                valor={data.roles_vigentes.vecino}
                acento="success"
                to="/superadmin/roles"
              />
              <MetricaCard
                titulo="Comisión"
                valor={data.roles_vigentes.comision}
                to="/superadmin/roles"
              />
              <MetricaCard
                titulo="Tesorería"
                valor={data.roles_vigentes.tesorero}
                to="/superadmin/roles"
              />
            </div>
          </Seccion>

          <Seccion titulo="Inventario de recursos físicos">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MetricaCard
                titulo="Controles"
                valor={data.recursos.por_tipo.control_remoto}
                subtitulo="CR-NNN"
              />
              <MetricaCard
                titulo="Llaveros"
                valor={data.recursos.por_tipo.llavero}
                subtitulo="LL-NNN"
              />
              <MetricaCard
                titulo="Citofonía"
                valor={data.recursos.por_tipo.citofonia}
                subtitulo="CT-NNN"
              />
              <MetricaCard
                titulo="Wi-Fi"
                valor={data.recursos.por_tipo.acceso_app_wifi}
                subtitulo="WF-NNN"
              />
              <MetricaCard
                titulo="Otros"
                valor={data.recursos.por_tipo.otro}
                subtitulo="OT-NNN"
              />
            </div>
            <div className="text-xs text-text-muted mt-2">
              {data.recursos.total} totales · {data.recursos.entregados}{' '}
              entregados
            </div>
          </Seccion>

          <Seccion titulo="Configuración financiera">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.configuracion_cuota.vigente &&
              data.configuracion_cuota.monto_semestral !== null ? (
                <MetricaCard
                  titulo="Cuota vigente"
                  valor={formatCLP(data.configuracion_cuota.monto_semestral)}
                  subtitulo={`por semestre · desde ${data.configuracion_cuota.vigente_desde}`}
                  acento="success"
                  to="/superadmin/configuracion-cuota"
                />
              ) : (
                <MetricaCard
                  titulo="Cuota vigente"
                  valor="—"
                  subtitulo="Sin configurar"
                  acento="danger"
                  to="/superadmin/configuracion-cuota"
                />
              )}
              <MetricaCard
                titulo="Categorías activas"
                valor={data.categorias.activas}
                subtitulo={`${data.categorias.inactivas} desactivadas`}
                to="/superadmin/categorias"
              />
              <MetricaCard
                titulo="Proyectos en curso"
                valor={data.proyectos.en_ejecucion + data.proyectos.aprobado}
                subtitulo={`${data.proyectos.propuesto} propuestos · ${data.proyectos.cerrado} cerrados`}
                to="/superadmin/proyectos"
              />
            </div>
          </Seccion>

          <Seccion titulo="Cuotas — estado actual">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MetricaCard
                titulo="Pendientes"
                valor={data.cuotas.pendientes}
                acento="warning"
              />
              <MetricaCard
                titulo="En abono"
                valor={data.cuotas.en_abono}
              />
              <MetricaCard
                titulo="Pagadas"
                valor={data.cuotas.pagadas}
                acento="success"
              />
              <MetricaCard
                titulo="Vencidas"
                valor={data.cuotas.vencidas}
                acento="danger"
              />
              <MetricaCard
                titulo="Condonadas"
                valor={data.cuotas.condonadas}
              />
            </div>
          </Seccion>
        </>
      )}
    </div>
  );
}
